import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-24edf720"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "flex mt-s1 align-items-center justify-content-end" }
const _hoisted_2 = { class: "mr-s3" }
const _hoisted_3 = { class: "text-sm" }

import { compareTokenAddresses } from '@/sdk/utils';
import { SelectedPortfolioTokens } from '@/store/modules/portfolios/models/selected-portfolio-tokens.interface';
import { BIG_ZERO } from '@/utils/bigNumber';
import { useEasyModeForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeForm';
import { useEasyModeAddLiquidityCalculation } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeAddLiquidityCalculation';
import { EasyModeAddLiquidityForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-form';
import { EasyModeAddLiquidityToken } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-token';
import BigNumber from 'bignumber.js';
import { computed, ComputedRef, ref, watch } from 'vue';
import { FilledTokensObject, Portfolio } from '@/sdk/entities/portfolio';
import DialogContent from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/common/DialogContent.vue';
import { useI18n } from 'vue-i18n';
import AddLiquidityInputPanel from './AddLiquidityInputPanel.vue';
import AddLiquidityNoticesList from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/add-liquidity/AddLiquidityNoticesList.vue';
import AddLiquidityConfirmation from '@/views/pages/liquidity/portfolios/portfolio/confirmation/AddLiquidityConfirmation.vue';
import TooltipIcon from '@/components/tooltip/TooltipIcon.vue';
import SwitchInput from '@/components/switch/SwitchInput.vue';
import LossInfo from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/LossInfo.vue';
import TokenList from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/tokens-list/TokenList.vue';
import { setNestedDialogOpen } from '@/composables/utils/useUtils';
import { useStore } from 'vuex';
import DestinationAddress from '@/components/DestinationAddress.vue';
import { useEasyModeAddLiquidity } from '@/store/modules/portfolios/easy-mode/add-liquidity/useEasyModeAddLiquidity';
import { Token } from '@/sdk/entities/token';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import NotEnoughCurrencyForFeeNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/NotEnoughCurrencyForFeeNotice.vue';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'EasyModeAddLiquidityDialog',
  props: {
    portfolio: null
  },
  emits: ["close", "confirm"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'close'): void;
  (e: 'confirm'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  portfolio: Portfolio;
}

const { setSelectedTokens, addLiquidityForm } = useEasyModeAddLiquidity();
const {
  initEasyModeForm,
  easyModeForm: easyModeFormCommon,
  forceDisabled,
  selectedTokens: selectedCommonTokens,
  disableTokensWithZeroBalance,
  getPortfolioAvailableTokens,
  crossChainLossUSD,
  needDestinationAddress,
} = useEasyModeForm();
const selectedTokens = selectedCommonTokens as ComputedRef<EasyModeAddLiquidityToken[]>;
const { calculateLimits } = useEasyModeAddLiquidityCalculation();
const { t } = useI18n();
const easyModeForm = easyModeFormCommon as EasyModeAddLiquidityForm;
const { state } = useStore();




initEasyModeForm({
  portfolio: props.portfolio,
  action: 'addLiquidity',
  baseOrLpTokenAmount: BIG_ZERO,
});
easyModeForm.destinationChainId = Number(DEFAULT_NETWORK_ID);

const addLiquidityConfirmationOpened = ref(false);
const destinationAddress = ref(easyModeForm.destinationAddress ?? '');

const tokensToDeposit = computed(() => {
  if (forceDisabled.value) {
    return [];
  }

  const portfolioTokens = getPortfolioAvailableTokens(props.portfolio);
  return selectedTokens.value
    .filter(formToken => formToken.amount?.gt(0))
    .map(
      token =>
        portfolioTokens.find(tokenInfo =>
          compareTokenAddresses(tokenInfo.tokenAddress, token.address),
        )?.token,
    )
    .filter<Token>((token): token is Token => !!token);
});

const portfolioForm: ComputedRef<SelectedPortfolioTokens> = computed(() => {
  return Object.fromEntries(
    selectedTokens.value
      .map(formToken => {
        const filledFormToken: FilledTokensObject = {
          value: formToken.amount.toFixed(),
          hasError: false,
          hasOnlyBalanceError: false,
          checked: true,
        };

        return [formToken.address, filledFormToken];
      })
      .filter(([, filledFormToken]) =>
        new BigNumber((filledFormToken as FilledTokensObject).value).gt(0),
      ),
  );
});

watch(() => easyModeForm.baseTokenAmount, calculateLimits);
watch(selectedTokens, calculateLimits);
watch(
  () => (easyModeForm as EasyModeAddLiquidityForm).withinBalances,
  value => {
    if (!value) {
      easyModeForm.tokens.forEach(token => (token.disabled = false));
    } else {
      disableTokensWithZeroBalance();
    }
    calculateLimits();
  },
);

const closeConfirmationModal = () => {
  addLiquidityConfirmationOpened.value = false;
  setNestedDialogOpen(addLiquidityConfirmationOpened.value);
};

const openConfirmationModal = () => {
  addLiquidityConfirmationOpened.value = true;
  setNestedDialogOpen(addLiquidityConfirmationOpened.value);
};

const onClose = () => {
  emits('close');
};

const onConfirm = () => {
  emits('confirm');
};

const sendTransaction = async () => {
  closeConfirmationModal();
  onClose();
  onConfirm();
};

const onChangeNetwork = event => {
  easyModeForm.destinationChainId = event.value;
  calculateLimits();
};

watch(
  () => props.portfolio,
  val => (easyModeForm.portfolio = val),
  {
    deep: true,
  },
);

watch([needDestinationAddress, destinationAddress], ([needAddress, address]) => {
  if (!ENABLE_FAKE_CARDANO_NETWORK) return;

  easyModeForm.destinationAddress = needAddress && address ? address : null;
  if (!needAddress) {
    destinationAddress.value = '';
  }
});

watch(portfolioForm, selectedTokens => {
  if (ENABLE_FAKE_CARDANO_NETWORK) {
    setSelectedTokens(selectedTokens);
  } else {
    addLiquidityForm.selectedTokens = selectedTokens;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(DialogContent, {
      portfolio: __props.portfolio,
      action: "addLiquidity",
      onCancel: onClose,
      onSubmit: openConfirmationModal,
      onChangeNetwork: _cache[2] || (_cache[2] = ($event: any) => (onChangeNetwork($event))),
      "default-chain-id": Number(_unref(DEFAULT_NETWORK_ID)),
      "chosen-tokens": _unref(tokensToDeposit),
      "is-notice-loading": false,
      "is-input-active": !_unref(easyModeForm).baseTokenAmount.gt(0),
      "force-disabled": _unref(forceDisabled)
    }, {
      toggleSwitch: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.$t('portfolio.addLiquidity.withinBalances')), 1),
            _createVNode(TooltipIcon, {
              tooltip: _ctx.$t('portfolio.tooltip.withinBalances'),
              inline: ""
            }, null, 8, ["tooltip"])
          ]),
          _createVNode(SwitchInput, {
            modelValue: _unref(easyModeForm).withinBalances,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(easyModeForm).withinBalances) = $event)),
            class: "switch-primary"
          }, null, 8, ["modelValue"])
        ])
      ]),
      inputBlock: _withCtx(() => [
        _createVNode(AddLiquidityInputPanel)
      ]),
      destinationAddress: _withCtx(() => [
        (_unref(needDestinationAddress))
          ? (_openBlock(), _createBlock(DestinationAddress, {
              key: 0,
              modelValue: destinationAddress.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((destinationAddress).value = $event)),
              class: "mt-s2"
            }, null, 8, ["modelValue"]))
          : _createCommentVNode("", true)
      ]),
      notice: _withCtx(() => [
        _createVNode(NotEnoughCurrencyForFeeNotice),
        _createVNode(AddLiquidityNoticesList, { portfolio: __props.portfolio }, null, 8, ["portfolio"])
      ]),
      lossInfo: _withCtx(() => [
        _createVNode(LossInfo)
      ]),
      tokenList: _withCtx(() => [
        _createVNode(TokenList, { currentAction: "addLiquidity" })
      ]),
      _: 1
    }, 8, ["portfolio", "default-chain-id", "chosen-tokens", "is-input-active", "force-disabled"]),
    _createVNode(_component_Dialog, {
      visible: addLiquidityConfirmationOpened.value,
      "onUpdate:visible": _cache[3] || (_cache[3] = ($event: any) => ((addLiquidityConfirmationOpened).value = $event)),
      header: _unref(t)('portfolio.addLiquidity.confirmDialog.header', { portfolioName: __props.portfolio.name }),
      modal: true,
      onHide: closeConfirmationModal,
      dismissableMask: "",
      "content-class": _unref(state).layout.isMobile ? 'scroll-container' : ''
    }, {
      default: _withCtx(() => [
        _createVNode(AddLiquidityConfirmation, {
          form: _unref(portfolioForm),
          portfolio: __props.portfolio,
          crossChainFeePercent: _unref(easyModeForm).crossChainLossPercent,
          crossChainFeeUSD: _unref(crossChainLossUSD),
          cancel: closeConfirmationModal,
          confirm: sendTransaction
        }, null, 8, ["form", "portfolio", "crossChainFeePercent", "crossChainFeeUSD"])
      ]),
      _: 1
    }, 8, ["visible", "header", "content-class"])
  ], 64))
}
}

})